import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OAuthFrontendService, type OAuthConsentHydrationResponse } from '@/features/Auth/services/oauth.service';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/shared/components/ui/alert';
import { Badge } from '@/shared/components/ui/badge';
import { LoadingSpinner } from '@/shared/components/ui/loading-spinner';
import { ArrowLeftRight } from 'lucide-react';

const scopeVariantMap: Record<string, 'default' | 'destructive' | 'secondary' | 'outline'> = {
  admin: 'destructive',
  execute: 'secondary',
  write: 'default',
  read: 'outline',
};

function WorkspaceOption({
  name,
  logoUrl,
  organizationName,
}: {
  name: string;
  logoUrl?: string | null;
  // Só é exibido quando os workspaces vêm de mais de uma organização (AB#84509):
  // sem isso, dois workspaces homônimos em empresas diferentes ficam indistinguíveis.
  organizationName?: string;
}) {
  return (
    <span className="flex items-center gap-2">
      {logoUrl ? (
        <img src={logoUrl} alt="" className="h-5 w-5 rounded object-cover flex-shrink-0" />
      ) : (
        <span className="h-5 w-5 rounded bg-muted flex items-center justify-center flex-shrink-0 text-[10px] font-semibold text-muted-foreground">
          {name.charAt(0).toUpperCase()}
        </span>
      )}
      <span className="flex flex-col leading-tight">
        <span>{name}</span>
        {organizationName && (
          <span className="text-xs text-muted-foreground">{organizationName}</span>
        )}
      </span>
    </span>
  );
}

export default function OAuthConsent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, loading, isAuthenticating } = useAuth();
  const [data, setData] = useState<OAuthConsentHydrationResponse | null>(null);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestId = searchParams.get('id') || '';

  useEffect(() => {
    if (loading || isAuthenticating) {
      return;
    }

    if (!requestId) {
      setError(t('auth.oauth.consent.errors.invalidRequest'));
      return;
    }

    if (!user) {
      sessionStorage.setItem('prodgy_redirect_after_login', window.location.pathname + window.location.search);
      navigate('/login', { replace: true });
      return;
    }

    const load = async () => {
      try {
        setError(null);
        const result = await OAuthFrontendService.getAuthorizationRequest(requestId);
        setData(result);
        setSelectedProductId(result.products[0]?.id || '');
      } catch (requestError) {
        const message = requestError instanceof Error ? requestError.message : null;
        setError(message || t('auth.oauth.consent.errors.loadFailed'));
      }
    };

    load();
  }, [user, loading, isAuthenticating, navigate, requestId, t]);

  const selectedWorkspace = data?.products.find((p) => p.id === selectedProductId);

  // Nome da organização só entra quando os workspaces vêm de mais de uma: com uma só,
  // repetir a empresa em cada linha é ruído.
  const showOrganization =
    new Set((data?.products ?? []).map((p) => p.organizationId).filter(Boolean)).size > 1;

  const handleConsent = async (approved: boolean) => {
    if (!requestId) return;

    try {
      setSubmitting(true);
      setError(null);

      const response = await OAuthFrontendService.submitConsent({
        authz_request_id: requestId,
        // A organização vem do workspace escolhido, não do perfil (AB#84509): quem
        // pertence a várias autoriza na empresa do workspace que selecionou. O backend
        // reconfere o vínculo — este valor é conveniência, não autorização.
        organization_id: approved
          ? (selectedWorkspace?.organizationId ?? data?.organizationId ?? undefined)
          : undefined,
        product_id: approved ? selectedProductId : undefined,
        approved,
      });

      const redirectUrl = response.redirect_url || response.redirectUrl;
      if (!redirectUrl) {
        throw new Error(t('auth.oauth.consent.errors.redirectFailed'));
      }

      window.location.assign(redirectUrl);
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : null;
      setError(message || t('auth.oauth.consent.errors.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || isAuthenticating || (!data && !error)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-2xl shadow-lg">
        <CardHeader className="space-y-3">
          {data && (
            <div className="flex items-center justify-center gap-4 pb-2">
              <div className="flex flex-col items-center gap-1.5">
                <img
                  src="/assets/logo.png"
                  alt="Prodgy"
                  className="h-12 w-12 rounded object-contain"
                />
                <span className="text-xs text-muted-foreground">Prodgy</span>
              </div>
              <ArrowLeftRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <div className="flex flex-col items-center gap-1.5">
                {data.client.logoUri ? (
                  <img
                    src={data.client.logoUri}
                    alt={data.client.name}
                    className="h-12 w-12 rounded object-contain"
                  />
                ) : (
                  <div className="h-12 w-12 rounded bg-muted flex items-center justify-center text-lg font-semibold text-muted-foreground">
                    {data.client.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-xs text-muted-foreground truncate max-w-[120px]">{data.client.name}</span>
              </div>
            </div>
          )}
          <CardTitle>{t('auth.oauth.consent.title')}</CardTitle>
          <CardDescription>
            {data
              ? t('auth.oauth.consent.description', { clientName: data.client.name })
              : t('auth.oauth.consent.errors.invalidRequest')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertTitle>{t('auth.oauth.error.title')}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {data && (
            <>
              <div className="space-y-3">
                <div className="text-sm font-medium">{t('auth.oauth.consent.requestedPermissions')}</div>
                <div className="flex flex-wrap gap-2">
                  {data.scopes.map((scope) => {
                    const action = scope.split(':').pop() || scope;
                    const key = scope.replace(':', '_');
                    return (
                      <Badge key={scope} variant={scopeVariantMap[action] || 'secondary'}>
                        {t(`auth.oauth.consent.scopeLabels.${key}`, scope)}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              {data.products.length > 1 ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium">{t('auth.oauth.consent.workspace')}</div>
                  <Select value={selectedProductId} onValueChange={setSelectedProductId} disabled={submitting}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('auth.oauth.consent.selectWorkspace')}>
                        {selectedWorkspace && (
                          <WorkspaceOption
                            name={selectedWorkspace.name}
                            logoUrl={selectedWorkspace.logoUrl}
                            organizationName={showOrganization ? selectedWorkspace.organizationName : undefined}
                          />
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {data.products.map((workspace) => (
                        <SelectItem key={workspace.id} value={workspace.id}>
                          <WorkspaceOption
                            name={workspace.name}
                            logoUrl={workspace.logoUrl}
                            organizationName={showOrganization ? workspace.organizationName : undefined}
                          />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : data.products.length === 1 && (
                <div className="flex items-center gap-3 rounded-lg border px-4 py-3">
                  {data.products[0].logoUrl ? (
                    <img src={data.products[0].logoUrl} alt="" className="h-8 w-8 rounded object-cover flex-shrink-0" />
                  ) : (
                    <span className="h-8 w-8 rounded bg-muted flex items-center justify-center flex-shrink-0 text-sm font-semibold text-muted-foreground">
                      {data.products[0].name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div>
                    <div className="text-xs text-muted-foreground">{t('auth.oauth.consent.workspace')}</div>
                    <div className="text-sm font-medium">{data.products[0].name}</div>
                  </div>
                </div>
              )}

              <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
                {t('auth.oauth.consent.disclaimer')}
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button variant="outline" disabled={submitting} onClick={() => handleConsent(false)}>
                  {t('auth.oauth.consent.cancel')}
                </Button>
                <Button
                  disabled={submitting || !selectedProductId}
                  onClick={() => handleConsent(true)}
                >
                  {submitting ? t('auth.oauth.connecting') : t('auth.oauth.consent.authorize')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
